class TypeaheadSelect extends HTMLElement {
  /** @type {HTMLSelectElement} */
  #select;

  /** @type {HTMLInputElement} */
  #input;

  /** @type {HTMLDivElement} */
  #list;

  /** @type {Array} */
  #suggestions = [];

  #activeIndex = -1;

  #debounceTimer = null;

  #src = null; // ASYNC FEATURE

  constructor () {
    super();
    this.attachShadow({ 'mode': 'open' });

    this.shadowRoot.innerHTML = `
      <style>
          .wrapper {
              position: relative;
              display: inline-block;
              width: 100%;
          }

          .tas-input {
              width: 100%;
              padding: 0.5em;
              box-sizing: border-box;
          }

          .tas-list {
              position: absolute;
              top: 100%;
              left: 0;
              right: 0;
              background: white;
              border: 1px solid #ccc;
              border-top: none;
              max-height: 180px;
              overflow-y: auto;
              display: none;
              z-index: 999;
          }

          .tas-item {
              padding: 0.5em;
              cursor: pointer;
          }

          .tas-item:hover,
          .tas-item.active {
              background: #eee;
          }

          .tas-highlight {
              font-weight: bold;
          }
      </style>

      <div class="wrapper">
          <slot></slot>
          <input type="text" class="tas-input" title="" placeholder="" />
          <div class="tas-list"></div>
      </div>
    `;
  }

  connectedCallback () {
    console.log('TypeaheadSelect connected');

    this.#src = this.getAttribute('data-source'); // ASYNC FEATURE
    this.#select = this.querySelector('select');
    this.#input = this.shadowRoot.querySelector('.tas-input');
    this.#list = this.shadowRoot.querySelector('.tas-list');

    this.#input.style.display = 'none';
    this.#init();
  }

  // -------- PUBLIC API --------

  set suggestions (data) {
    this.#suggestions = Array.isArray(data) ? data : [];
    this.#refreshList('');
  }

  get suggestions () {
    return this.#suggestions;
  }

  set src (url) { // ASYNC FEATURE
    this.#src = url;
  }

  get src () { // ASYNC FEATURE
    return this.#src;
  }

  // -------- SETUP --------

  #init () {
    if (!this.#select) return;

    // Hide the select, show the input
    this.#select.style.display = 'none';
    this.#input.style.display = 'block';

    const selected = this.#select.options[this.#select.selectedIndex];
    this.#input.value = selected?.textContent ?? '';

    // Input event
    this.#input.addEventListener('input', () => {
      const value = this.#input.value;
      console.log('TypeaheadSelect input changed:', value);

      this.dispatchEvent(new CustomEvent('input-changed', { 'detail': { value } }));

      // ASYNC FEATURE: debounce and fetch
      if (this.#src) {
        this.#debouncedFetch(value);
      } else {
        this.#refreshList(value);
      }
    });

    this.#input.addEventListener('focus', () => {
      this.#refreshList(this.#input.value);
    });

    this.#input.addEventListener('keydown', (e) => this.#handleKey(e));

    this.#input.addEventListener('blur', () => {
      setTimeout(() => this.#autoSelect(), 150);
    });

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.#list.style.display = 'none';
      }
    });
  }

  // -------- ASYNC FEATURE: debounce wrapper --------

  /**
   *
   * @param {string} query
   */
  #debouncedFetch (query) {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#fetchSuggestions(query);
    }, 250);
  }

  // -------- ASYNC FEATURE: fetch suggestions --------

  /**
   *
   * @param {string} query
   * @returns
   */
  async #fetchSuggestions (query) {
    console.log('TypeaheadSelect fetchSuggestions:', query);

    if (!this.#src) return;

    try {
      const url = `${this.#src}?q=${encodeURIComponent(query)}`;

      const response = await fetch(url, {
        'headers': {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Network error');

      const data = await response.json();

      if (Array.isArray(data)) {
        this.#suggestions = data;
        this.#refreshList(query);
      }
    } catch (err) {
      console.error('TypeaheadSelect fetch error:', err);
    }
  }

  // -------- KEYBOARD --------

  #handleKey (e) {
    const items = [...this.#list.querySelectorAll('.tas-item')];
    const len = items.length;
    if (!len) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.#activeIndex = (this.#activeIndex + 1) % len;
        this.#updateActive(items);
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.#activeIndex = (this.#activeIndex - 1 + len) % len;
        this.#updateActive(items);
        break;

      case 'Enter':
        if (this.#activeIndex >= 0 && items[this.#activeIndex]) {
          e.preventDefault();
          items[this.#activeIndex].click();
        }
        break;

      case 'Escape':
        this.#list.style.display = 'none';
        break;
    }
  }

  /**
   *
   * @param {Array} items
   */
  #updateActive (items) {
    items.forEach(item => item.classList.remove('active'));
    if (this.#activeIndex >= 0) {
      const activeItem = items[this.#activeIndex];
      activeItem.classList.add('active');
      activeItem.scrollIntoView({ 'block': 'nearest' });
    }
  }

  // -------- HIGHLIGHT --------

  /**
   *
   * @param {string} text
   * @param {string} query
   */
  #highlight (text, query) {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;

    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + query.length);
    const after = text.slice(idx + query.length);

    return `${before}<span class="tas-highlight">${match}</span>${after}`;
  }

  // -------- SUGGESTION LIST --------

  /**
   * @param {string} query
   */
  #refreshList (query) {
    console.log('TypeaheadSelect refreshList:', query);

    const q = query.toLowerCase();

    const filtered = this.#suggestions.filter(item =>
      item.name.toLowerCase().includes(q)
    );

    this.#list.innerHTML = '';
    this.#activeIndex = -1;
    this.#list.style.display = filtered.length ? 'block' : 'none';

    filtered.forEach(item => {
      const div = document.createElement('div');
      div.className = 'tas-item';
      div.dataset.value = item.value;
      div.dataset.name = item.name;
      div.innerHTML = this.#highlight(item.name, query);

      div.addEventListener('click', () => this.#selectItem(item));
      this.#list.appendChild(div);
    });
  }

  // -------- SELECTION --------

  /**
   * TODO: define a class for this?
   * @param {object} item from the suggestions array
   */
  #selectItem (item) {
    this.#input.value = item.name;
    this.#select.value = item.value;
    this.#list.style.display = 'none';

    this.#select.dispatchEvent(new Event('change', { 'bubbles': true }));

    this.dispatchEvent(new CustomEvent('item-selected', {
      'detail': {
        'name': item.name,
        'value': item.value
      }
    }));
  }

  // -------- AUTO-SELECT --------

  #autoSelect () {
    const value = this.#input.value.trim().toLowerCase();
    if (!value) return;

    const match = this.#suggestions.find(item =>
      item.name.toLowerCase().startsWith(value)
    );

    if (match) {
      this.#selectItem(match);
    } else {
      this.#select.value = '';
    }
  }
}

customElements.define('typeahead-select', TypeaheadSelect);
